// --------------------------------
// Format a number of cents into USD currency format
// --------------------------------
export function formatMoney(cents) {
  return `$${(cents / 100).toFixed(2)}`;
}

// --------------------------------
// Generate HTML for a single cart item
// Includes image if it is present, title, quantity, and price
// --------------------------------
export function buildCartItemHTML(item) {
  return `
    <div class="cart-preview-item">
      ${
        item.image
          ? `<img src="${item.image}" alt="${item.title}" class="cart-item-image">`
          : ""
      }
      <div class="cart-item-details">
        <h4>${item.title}</h4>
        <p>Qty: ${item.quantity}</p>
        <p class="price">${formatMoney(item.line_price)}</p>
      </div>
    </div>
  `;
}

// --------------------------------
// Create full HTML for cart preview modal
// Limits visible items to 3 and adds scroll when over 3 item limit
// --------------------------------
export function buildCartHTML(items, totalPrice, addToCart = false, cartItemKey = null) {
  const MAX_VISIBLE_ITEMS = 3;
  const hasMultipleItems = items.length > 1;
  const isScrollable = items.length > MAX_VISIBLE_ITEMS;

  let displayedItems = items;

  // Check if it is an add-to-cart preview
  // If so, filter out the item being added if it exists
  if (addToCart && cartItemKey) {
    displayedItems = items.filter(item => item.key !== cartItemKey);
  }

  const cartItemsHTML = displayedItems.map(buildCartItemHTML).join("");

  // Show total price only if there are multiple items and is add-to-cart preview or if addToCart is false
  const shouldShowTotal = !addToCart || (addToCart && hasMultipleItems);

  return `
    <div class="cart-preview">
      <div class="cart-items-wrapper${isScrollable ? " scrollable" : ""}">
        ${cartItemsHTML}
      </div>
      ${
        shouldShowTotal
          ? `
        <div class="cart-total">
          <p id="cart-total">Total: ${formatMoney(totalPrice)}</p>
        </div>
      `
          : ""
      }
    </div>
  `;
}

// --------------------------------
// Fetch cart data from Shopify's /cart.js
// Returns a cart object or throws an error on failure
// --------------------------------
export async function fetchCartData() {
  const response = await fetch("/cart.js");

  if (!response.ok) throw new Error("Failed to fetch cart data");

  return await response.json();
}

// --------------------------------
// Show the cart modal and implement HTML content
// --------------------------------
export function showCartModal(html) {
  const cartMessage = document.getElementById("cart-message");
  const cartItem = document.getElementById("cart-item");

  if (!cartMessage || !cartItem) {
    console.error("Cart modal elements missing");
    return;
  }

  cartItem.innerHTML = html;
  cartMessage.style.display = "block";
  cartMessage.classList.add("active");
}

// --------------------------------
// Hide the cart modal
// --------------------------------
export function hideCartModal() {
  const cartMessage = document.getElementById("cart-message");

  if (cartMessage) {
    cartMessage.classList.remove("active");
    setTimeout(() => {
      cartMessage.style.display = "none";
    }, 300);
  }
}

// --------------------------------
// Generic function to fetch cart data and display a preview modal
// Behavior is controlled by options passed in
// --------------------------------
async function showCartPreview({ addToCart = false, useAddToCartWrapper = false, cartItemKey = null }) {
  try {
    const cartData = await fetchCartData();

    if (!cartData.items?.length) {
      hideCartModal();
      return;
    }

    const html = buildCartHTML(cartData.items, cartData.total_price, addToCart, cartItemKey);
    const wrapper = document.getElementById("cart-preview-wrapper");

    if (useAddToCartWrapper) {
      // Show in the dedicated add-to-cart preview wrapper
      if (wrapper) {
        wrapper.innerHTML = html;
        wrapper.style.display = "block";
      }
    } else {
      // Show in the main cart modal
      showCartModal(html);

      // Hide add-to-cart preview wrapper if it was open
      if (wrapper) {
        wrapper.innerHTML = "";
        wrapper.style.display = "none";
      }
    }
  } catch (error) {
    console.error(
      addToCart ? "Add-to-cart error:" : "Cart hover error:",
      error
    );
  }
}

// --------------------------------
// Show preview modal after adding an item to the cart
// --------------------------------
export function showAddToCartPreview(cartItemKey) {
  return showCartPreview({ addToCart: true, useAddToCartWrapper: true, cartItemKey });
}

// --------------------------------
// Show preview modal on hover
// --------------------------------
export function showCartHoverPreview() {
  return showCartPreview({ addToCart: false, useAddToCartWrapper: false });
}
